import { observer } from 'mobx-react-lite';
import { assertNever } from 'owa-assert';
import { CalendarList } from 'owa-calendar-list';
import { AttendeeTracking, EventDetails } from 'owa-event-details';
import { NotificationBarHost } from 'owa-notification-bar';
import { getCurrentPanelView } from 'owa-time-panel-view-stack';
import { ComposeForm } from 'owa-time-panel-compose';
import { ConflictsView, getHighlightedEventId } from 'owa-conflicts-view';
import { TodoPanel } from 'owa-todos';
import { popView } from 'owa-time-panel-bootstrap';
import * as React from 'react';
import {
    clearTelemetryData,
    initializeTelemetryData,
    panelClosed,
    panelOpened,
    pushNewView,
} from '../actions/timePanelStoreActions';
import { KeyCodes } from '@uifabric/utilities';
import { TimePanelDropListener } from 'owa-time-panel-dnd';

const styles = require('./TimePanel.scss');

const todoPanelStyles = {
    rootClassName: styles.todoPanel,
    folderPickerClassName: styles.todoPanelFolderPicker,
    folderPickerStyles: {
        commandButtonClassName: styles.todoPanelFolderPickerCommandButton,
        commandButtonTextLabelClassName: styles.todoPanelFolderPickerCommandButtonTextLabel,
        commandButtonIconClassName: styles.todoPanelFolderPickerCommandButtonIcon,
        commandButtonFlexContainerClassName: styles.todoPanelFolderPickerCommandButtonFlexContainer,
    },
};

export interface TimePanelProps {
    closePanel?: () => void;
}

export default observer(function TimePanel(props: TimePanelProps) {
    React.useEffect(() => {
        // action ordering matters for this effect to accurately log telemetry
        initializeTelemetryData();
        panelOpened();

        return () => {
            panelClosed();
            clearTelemetryData();
        };
    }, []);

    const onKeyDownCallback = React.useCallback(
        (ev: React.KeyboardEvent<HTMLElement>) => {
            switch (ev.keyCode) {
                case KeyCodes.escape:
                    // If escape handling got to here, close the panel
                    props.closePanel && props.closePanel();
                    break;
            }
        },
        [props.closePanel]
    );

    const panelView = getCurrentPanelView();

    let content: JSX.Element = null;
    switch (panelView) {
        case 'Calendar':
            content = <CalendarList source={'TimePanel'} />;
            break;
        case 'EventDetails':
            content = (
                <EventDetails
                    onDismiss={popView}
                    onAttendeeTrackingClicked={onAttendeeTrackingClicked}
                />
            );
            break;
        case 'AttendeeTracking':
            content = <AttendeeTracking onDismiss={popView} />;
            break;
        case 'Conflicts':
            content = (
                <ConflictsView
                    hideHeader={false}
                    headerClassName={styles.conflictsHeader}
                    highlightedEventId={getHighlightedEventId()}
                />
            );
            break;
        case 'Tasks':
            content = (
                <TodoPanel source={'TimePanel'} hideFolderPicker={false} styles={todoPanelStyles} />
            );
            break;
        case 'ComposeForm':
            content = <ComposeForm />;
            break;

        default:
            assertNever(panelView);
    }

    return (
        <TimePanelDropListener>
            <div className={styles.container} onKeyDown={onKeyDownCallback}>
                <div className={styles.contentWrapper}>{content}</div>
                <div className={styles.notificationBarHostWrapper}>
                    <NotificationBarHost hostId={'TimePanelNotificationBarHost'} />
                </div>
            </div>
        </TimePanelDropListener>
    );
});

function onAttendeeTrackingClicked() {
    pushNewView('AttendeeTracking');
}
